import { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { ThreeService, ThreeConfig } from '../service/threeService';
import { Point3D, Chart3DData } from '../service/railwayDataService';

export interface TooltipData {
  visible: boolean;
  x: number;
  y: number;
  content: any;
  point?: Point3D;
}

export interface UseThree3DOptions {
  enableControls?: boolean;
  enableTooltips?: boolean;
  backgroundColor?: string;
  cameraPosition?: { x: number; y: number; z: number };
  onPointClick?: (point: Point3D) => void;
  onPointHover?: (point: Point3D | null) => void;
  autoRotate?: boolean;
  enableAnimation?: boolean;
}

export interface UseThree3DReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  error: string | null;
  tooltip: TooltipData;
  updateData: (data: Chart3DData) => void;
  resetCamera: () => void;
  clearScene: () => void;
  exportImage: () => Promise<string>;
  toggleAutoRotate: () => void;
  setSelectedMetrics: (metrics: string[]) => void;
  statistics: {
    totalPoints: number;
    visiblePoints: number;
    fps: number;
  };
}

export const useThree3D = (options: UseThree3DOptions = {}): UseThree3DReturn => {
  const containerRef = useRef<HTMLDivElement>(null);
  const threeServiceRef = useRef<ThreeService | null>(null);
  const pointMeshesRef = useRef<THREE.Mesh[]>([]);
  const currentDataRef = useRef<Chart3DData | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const fpsCounterRef = useRef({ frame: 0, time: performance.now(), fps: 60 });
  const initializationRef = useRef<boolean>(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData>({
    visible: false,
    x: 0,
    y: 0,
    content: null
  });
  const [statistics, setStatistics] = useState({
    totalPoints: 0,
    visiblePoints: 0,
    fps: 60
  });

  const {
    enableControls = true,
    enableTooltips = true,
    backgroundColor = '#1a1a2e',
    cameraPosition = { x: 50, y: 40, z: 50 },
    onPointClick,
    onPointHover,
    autoRotate = false,
    enableAnimation = true
  } = options;
  

  // Configurar event listeners con mejor manejo de eventos
  const setupEventListeners = useCallback(() => {
    if (!containerRef.current || !threeServiceRef.current) return;

    const container = containerRef.current;
    const threeService = threeServiceRef.current;

    // Mouse events para tooltips y clicks
    const handleMouseMove = (event: MouseEvent) => {
      if (!enableTooltips) return;

      try {
        const intersectedObject = threeService.getIntersectedObject(
          event.clientX,
          event.clientY
        );

        if (intersectedObject && intersectedObject.userData.point) {
          const point = intersectedObject.userData.point as Point3D;
          
          setTooltip({
            visible: true,
            x: event.clientX,
            y: event.clientY,
            content: point.metadata,
            point
          });

          onPointHover?.(point);
          container.style.cursor = 'pointer';
        } else {
          setTooltip(prev => ({ ...prev, visible: false }));
          onPointHover?.(null);
          container.style.cursor = 'default';
        }
      } catch (err) {
        console.warn('Error en mouse move:', err);
      }
    };

    const handleClick = (event: MouseEvent) => {
      if (!threeService) return;

      try {
        const intersectedObject = threeService.getIntersectedObject(
          event.clientX,
          event.clientY
        );

        if (intersectedObject && intersectedObject.userData.point) {
          const point = intersectedObject.userData.point as Point3D;
          onPointClick?.(point);
        }
      } catch (err) {
        console.warn('Error en click:', err);
      }
    };

    // Resize observer con debounce
    let resizeTimeout: NodeJS.Timeout;
    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            threeService.resize(width, height);
          }
        }
      }, 100);
    });

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('click', handleClick);
    resizeObserver.observe(container);

    // Cleanup function
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('click', handleClick);
      resizeObserver.disconnect();
      clearTimeout(resizeTimeout);
    };
  }, [enableTooltips, onPointClick, onPointHover]);

  // Render loop con FPS counter mejorado
  const startRenderLoop = useCallback(() => {
    if (!threeServiceRef.current) return;

    const threeService = threeServiceRef.current;
    
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      try {
        // FPS Counter
        const now = performance.now();
        fpsCounterRef.current.frame++;
        
        if (now >= fpsCounterRef.current.time + 1000) {
          const fps = Math.round((fpsCounterRef.current.frame * 1000) / (now - fpsCounterRef.current.time));
          fpsCounterRef.current.fps = fps;
          fpsCounterRef.current.frame = 0;
          fpsCounterRef.current.time = now;
          
          setStatistics(prev => ({ ...prev, fps }));
        }

        // Auto rotate
        if (autoRotate && threeService.getControls()) {
          const controls = threeService.getControls();
          controls.autoRotate = true;
          controls.autoRotateSpeed = 0.5;
        }

        // Animaciones de puntos
        if (enableAnimation) {
          animatePoints();
        }

        // Renderizar
        threeService.getRenderer().render(threeService.getScene(), threeService.getCamera());
      } catch (err) {
        console.warn('Error en render loop:', err);
      }
    };

    animate();
  }, [autoRotate, enableAnimation]);

  // Animar puntos con efectos mejorados
  const animatePoints = useCallback(() => {
    const time = performance.now() * 0.001;
    
    pointMeshesRef.current.forEach((mesh, index) => {
      if (mesh.userData.point) {
        const point = mesh.userData.point as Point3D;
        
        // Efecto de "respiración" para puntos críticos
        if (point.metadata.recommendation_priority === 'CRITICAL') {
          const scale = 1 + Math.sin(time * 4 + index * 0.1) * 0.2;
          mesh.scale.setScalar(scale);
        }
        
        // Efecto de rotación suave para puntos técnicos
        if (point.metadata.type === 'technical') {
          mesh.rotation.y += 0.005;
        }

        // Efecto de hover
        if (mesh.userData.isHovered) {
          const hoverScale = 1.3 + Math.sin(time * 6) * 0.1;
          mesh.scale.setScalar(hoverScale);
        }
      }
    });
  }, []);

  // Actualizar datos del gráfico con mejor gestión de memoria
  const updateData = useCallback((data: Chart3DData) => {
    if (!threeServiceRef.current) {
      console.warn('ThreeService no disponible para updateData');
      return;
    }

    currentDataRef.current = data;
    const threeService = threeServiceRef.current;

    console.log('📊 Actualizando datos 3D:', { points: data.points.length });

    // Limpiar puntos anteriores con disposición de memoria
    pointMeshesRef.current.forEach(mesh => {
      threeService.removeObject(mesh);
      mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else {
        mesh.material.dispose();
      }
    });
    pointMeshesRef.current = [];

    // Crear nuevos puntos con mejor gestión de geometrías
    const newMeshes: THREE.Mesh[] = [];
    const geometryCache = new Map<number, THREE.SphereGeometry>();
    
data.points.forEach((point, index) => {
  try {
    // Cache de geometrías para mejor performance
    const sizeKey = Math.round(point.size * 10);
    let geometry = geometryCache.get(sizeKey);
    if (!geometry) {
      geometry = new THREE.SphereGeometry(point.size, 16, 12);
      geometryCache.set(sizeKey, geometry);
    }
    
    // Material con propiedades visuales mejoradas
    const material = new THREE.MeshPhongMaterial({
      color: point.color,
      transparent: true,
      opacity: 0.85,
      shininess: 100,
      specular: 0x444444,
      emissive: point.metadata.recommendation_priority === 'CRITICAL' ? 
        new THREE.Color(point.color).multiplyScalar(0.1) : 
        new THREE.Color(0x000000)
    });

    // Crear mesh
    const mesh = new THREE.Mesh(geometry, material);
    
    // CORRECCIÓN: Posicionar SIN restar offset para evitar coordenadas negativas
    // ANTES (problemático):
    // const offsetX = (data.bounds.maxX + data.bounds.minX) / 2;
    // const offsetY = (data.bounds.maxY + data.bounds.minY) / 2;
    // const offsetZ = (data.bounds.maxZ + data.bounds.minZ) / 2;
    // mesh.position.set(point.x - offsetX, point.y - offsetY, point.z - offsetZ);
    
    // DESPUÉS (corregido):
    // Normalizar coordenadas al rango [-50, 50] para mantener centrado
    const normalizeCoord = (value: number, min: number, max: number) => {
      if (max === min) return 0; // Evitar división por cero
      return ((value - min) / (max - min)) * 100 - 50; // Rango [-50, 50]
    };
    
    const normalizedX = normalizeCoord(point.x, data.bounds.minX, data.bounds.maxX);
    const normalizedY = Math.max(0, normalizeCoord(point.y, data.bounds.minY, data.bounds.maxY)); // Y siempre >= 0
    const normalizedZ = normalizeCoord(point.z, data.bounds.minZ, data.bounds.maxZ);
    
    mesh.position.set(normalizedX, normalizedY, normalizedZ);
    
    console.log(`🎯 Hotspot ${index} positioned at:`, {
      original: { x: point.x, y: point.y, z: point.z },
      normalized: { x: normalizedX, y: normalizedY, z: normalizedZ },
      location: point.metadata.location
    });
    
    // Guardar referencia al punto
    mesh.userData = { 
      point, 
      index,
      isHovered: false,
      originalScale: 1
    };
    
    // Efecto de entrada con animación escalonada
    if (enableAnimation) {
      mesh.scale.setScalar(0);
      
      setTimeout(() => {
        const animateScale = () => {
          const currentScale = mesh.scale.x;
          const targetScale = 1;
          const newScale = THREE.MathUtils.lerp(currentScale, targetScale, 0.1);
          mesh.scale.setScalar(newScale);
          
          if (Math.abs(newScale - targetScale) > 0.01) {
            requestAnimationFrame(animateScale);
          }
        };
        animateScale();
      }, index * 30);
    }

    newMeshes.push(mesh);
    threeService.addObject(mesh);
    
  } catch (err) {
    console.warn(`Error creando mesh para punto ${index}:`, err);
  }
});

    pointMeshesRef.current = newMeshes;

    // Actualizar estadísticas
    setStatistics(prev => ({
      ...prev,
      totalPoints: data.points.length,
      visiblePoints: data.points.length
    }));

    // Ajustar cámara para ver todos los puntos
    if (data.points.length > 0 && enableControls) {
      autoFitCamera(data);
    }

    console.log('✅ Datos 3D actualizados:', { 
      meshes: newMeshes.length, 
      bounds: data.bounds 
    });

  }, [enableAnimation, enableControls]);

  // Auto-ajustar cámara con cálculo inteligente
  const autoFitCamera = useCallback((data: Chart3DData) => {
    if (!threeServiceRef.current || pointMeshesRef.current.length === 0) return;

    try {
      const threeService = threeServiceRef.current;
      const camera = threeService.getCamera();
      const controls = threeService.getControls();

      // Calcular bounding box de los meshes
      const box = new THREE.Box3();
      pointMeshesRef.current.forEach(mesh => {
        box.expandByObject(mesh);
      });

      if (!box.isEmpty()) {
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        
        // Calcular distancia óptima basada en FOV
        const fov = camera.fov * Math.PI / 180;
        const distance = Math.abs(maxDim / Math.sin(fov / 2)) * 1.5;
        
        // Posicionar cámara en vista isométrica
        const theta = Math.PI / 4; // 45 grados
        const phi = Math.PI / 6;   // 30 grados
        
        camera.position.set(
          center.x + distance * Math.sin(theta) * Math.cos(phi),
          center.y + distance * Math.sin(phi),
          center.z + distance * Math.cos(theta) * Math.cos(phi)
        );
        
        if (controls) {
          controls.target.copy(center);
          controls.update();
        } else {
          camera.lookAt(center);
        }
        
        console.log('📷 Cámara ajustada:', { center, distance, maxDim });
      }
    } catch (err) {
      console.warn('Error ajustando cámara:', err);
    }
  }, []);

  // Reset cámara con animación
  const resetCamera = useCallback(() => {
    if (!threeServiceRef.current) return;

    try {
      threeServiceRef.current.resetCamera();
      
      if (currentDataRef.current && currentDataRef.current.points.length > 0) {
        setTimeout(() => {
          autoFitCamera(currentDataRef.current!);
        }, 100);
      }
      
      console.log('🏠 Cámara reseteada');
    } catch (err) {
      console.warn('Error reseteando cámara:', err);
    }
  }, [autoFitCamera]);

  // Limpiar escena con disposición completa de memoria
  const clearScene = useCallback(() => {
    if (!threeServiceRef.current) return;

    try {
      // Limpiar meshes con disposición de memoria
      pointMeshesRef.current.forEach(mesh => {
        mesh.geometry.dispose();
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach(mat => mat.dispose());
        } else {
          mesh.material.dispose();
        }
      });
      pointMeshesRef.current = [];

      // Limpiar solo los puntos, mantener grid y ejes
      const scene = threeServiceRef.current.getScene();
      const objectsToRemove: THREE.Object3D[] = [];
      
      scene.traverse((child) => {
        if (child.userData && child.userData.point) {
          objectsToRemove.push(child);
        }
      });
      
      objectsToRemove.forEach(obj => scene.remove(obj));
      
      setStatistics(prev => ({
        ...prev,
        totalPoints: 0,
        visiblePoints: 0
      }));
      
      console.log('🧹 Escena limpiada');
    } catch (err) {
      console.warn('Error limpiando escena:', err);
    }
  }, []);

  // Exportar imagen con mejor calidad
  const exportImage = useCallback(async (): Promise<string> => {
    if (!threeServiceRef.current) {
      throw new Error('Three.js no inicializado');
    }

    try {
      const renderer = threeServiceRef.current.getRenderer();
      const originalSize = renderer.getSize(new THREE.Vector2());
      
      // Renderizar en alta resolución
      const scale = 2;
      renderer.setSize(originalSize.x * scale, originalSize.y * scale);
      
      // Forzar un render
      const scene = threeServiceRef.current.getScene();
      const camera = threeServiceRef.current.getCamera();
      renderer.render(scene, camera);
      
      const dataURL = renderer.domElement.toDataURL('image/png', 1.0);
      
      // Restaurar tamaño original
      renderer.setSize(originalSize.x, originalSize.y);
      
      console.log('📸 Imagen exportada en alta resolución');
      return dataURL;
    } catch (err) {
      console.error('Error exportando imagen:', err);
      throw err;
    }
  }, []);

  // Toggle auto rotate con validación
  const toggleAutoRotate = useCallback(() => {
    if (!threeServiceRef.current) return;
    
    try {
      const controls = threeServiceRef.current.getControls();
      if (controls) {
        controls.autoRotate = !controls.autoRotate;
        console.log('🔄 Auto rotate:', controls.autoRotate ? 'ON' : 'OFF');
      }
    } catch (err) {
      console.warn('Error toggling auto rotate:', err);
    }
  }, []);

  // Filtrar métricas visibles con animación
  const setSelectedMetrics = useCallback((metrics: string[]) => {
    if (!currentDataRef.current) return;

    try {
      let visibleCount = 0;
      
      pointMeshesRef.current.forEach(mesh => {
        const point = mesh.userData.point as Point3D;
        const shouldShow = !point.metadata.metric || metrics.includes(point.metadata.metric);
        
        if (mesh.visible !== shouldShow) {
          mesh.visible = shouldShow;
          
          // Animación de fade in/out
          if (enableAnimation) {
            const targetOpacity = shouldShow ? 0.85 : 0;
            const material = mesh.material as THREE.MeshPhongMaterial;
            
            const animateOpacity = () => {
              const currentOpacity = material.opacity;
              const newOpacity = THREE.MathUtils.lerp(currentOpacity, targetOpacity, 0.1);
              material.opacity = newOpacity;
              
              if (Math.abs(newOpacity - targetOpacity) > 0.01) {
                requestAnimationFrame(animateOpacity);
              }
            };
            animateOpacity();
          }
        }
        
        if (shouldShow) visibleCount++;
      });

      setStatistics(prev => ({ ...prev, visiblePoints: visibleCount }));
      console.log('👁️ Métricas filtradas:', { metrics, visibleCount });
    } catch (err) {
      console.warn('Error filtrando métricas:', err);
    }
  }, [enableAnimation]);

   const initializeThree = useCallback(async () => {
  console.log('🚀 [DEBUG] initializeThree called');
  console.log('🚀 [DEBUG] containerRef.current:', !!containerRef.current);
  console.log('🚀 [DEBUG] initializationRef.current:', initializationRef.current);

  if (!containerRef.current) {
    console.error('❌ [DEBUG] No containerRef.current available');
    return;
  }

  if (initializationRef.current) {
    console.warn('⚠️ [DEBUG] Already initializing, skipping');
    return;
  }

  try {
    setIsLoading(true);
    setError(null);
    initializationRef.current = true;

    console.log('🚀 [DEBUG] Starting Three.js initialization...');

    // VERIFICAR WEBGL
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      throw new Error('WebGL no está disponible en este navegador');
    }
    console.log('✅ [DEBUG] WebGL available');

    const rect = containerRef.current.getBoundingClientRect();
    console.log('📐 [DEBUG] Container rect:', rect);

    if (rect.width === 0 || rect.height === 0) {
      console.warn('⚠️ [DEBUG] Container has zero size, using defaults');
    }

    const config: ThreeConfig = {
      container: containerRef.current,
      width: rect.width || 800,
      height: rect.height || 600,
      backgroundColor,
      cameraPosition,
      enableControls: false // CAMBIO: No pasar enableControls al constructor
    };

    console.log('⚙️ [DEBUG] Three config:', config);

    // VERIFICAR QUE THREESERVICE EXISTE
    console.log('🔧 [DEBUG] About to create ThreeService...');
    
    if (typeof ThreeService !== 'function') {
      throw new Error('ThreeService no está disponible o no es una función');
    }

    // CREAR THREESERVICE (solo parte sincrónica)
    const threeService = new ThreeService(config);
    console.log('✅ [DEBUG] ThreeService created successfully');
    
    threeServiceRef.current = threeService;

    // Verificar que el service se creó correctamente
    if (!threeService.getScene || !threeService.getRenderer || !threeService.getCamera) {
      throw new Error('ThreeService no tiene los métodos requeridos');
    }

    console.log('🎮 [DEBUG] Adding grid and axes...');
    // Configurar scene básico
    threeService.addGrid(100, 20);
    threeService.addAxes(50);

    // CAMBIO CRÍTICO: Ahora completar la inicialización async
    console.log('🔄 [DEBUG] Completing async initialization...');
    await threeService.initialize(enableControls);
    console.log('✅ [DEBUG] Async initialization completed');

    console.log('🎧 [DEBUG] Setting up event listeners...');
    // Event listeners
    setupEventListeners();

    console.log('🔄 [DEBUG] Starting render loop...');
    // Iniciar render loop
    startRenderLoop();

    // PUNTO CRÍTICO: Ahora sí está completamente listo
    console.log('✅ [DEBUG] Setting isLoading to false');
    setIsLoading(false);
    console.log('✅ [DEBUG] Three.js inicializado correctamente');

  } catch (err) {
    console.error('❌ [DEBUG] Error en initializeThree:', err);
    console.error('❌ [DEBUG] Error stack:', err?.stack);
    setError(err instanceof Error ? err.message : 'Error inicializando Three.js');
    setIsLoading(false);
    initializationRef.current = false;
  }
}, [backgroundColor, cameraPosition, enableControls, setupEventListeners, startRenderLoop]);



  // Efecto de inicialización
useEffect(() => {
  console.log('🔄 [DEBUG] useEffect initialization triggered');
  console.log('🔄 [DEBUG] containerRef available:', !!containerRef.current);
  
  // Función async wrapper
  const initializeAsync = async () => {
    if (containerRef.current) {
      console.log('🔄 [DEBUG] Container is ready, calling initializeThree');
      await initializeThree();
    } else {
      console.warn('⚠️ [DEBUG] Container not ready yet');
      
      // Retry después de un breve delay
      setTimeout(async () => {
        console.log('🔄 [DEBUG] Retry: checking container again');
        if (containerRef.current) {
          console.log('🔄 [DEBUG] Container ready on retry, calling initializeThree');
          await initializeThree();
        } else {
          console.error('❌ [DEBUG] Container still not ready after timeout');
          setError('Container element no está disponible');
          setIsLoading(false);
        }
      }, 100000);
    }
  };

  initializeAsync();

  return () => {
    console.log('🧹 [DEBUG] useEffect cleanup triggered');
    // Cleanup completo
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (threeServiceRef.current) {
      console.log('🧹 [DEBUG] Disposing ThreeService');
      threeServiceRef.current.dispose();
    }
    
    // Limpiar meshes
    pointMeshesRef.current.forEach(mesh => {
      mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else {
        mesh.material.dispose();
      }
    });
    
    initializationRef.current = false;
    console.log('🧹 [DEBUG] useThree3D cleanup completado');
  };
}, []);

  // NUEVO: Log del estado isLoading
  useEffect(() => {
    console.log('📊 [DEBUG] isLoading changed to:', isLoading);
  }, [isLoading]);

  // NUEVO: Log del estado error
  useEffect(() => {
    if (error) {
      console.error('📊 [DEBUG] Error state changed to:', error);
    }
  }, [error]);

  // Efecto de resize con manejo de errores
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current && threeServiceRef.current) {
        try {
          const rect = containerRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            threeServiceRef.current.resize(rect.width, rect.height);
          }
        } catch (err) {
          console.warn('Error en resize:', err);
        }
      }
    };

    // Debounce del resize
    let resizeTimeout: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  // Log de performance en desarrollo
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const interval = setInterval(() => {
        if (statistics.fps < 30) {
          console.warn('⚠️ Performance bajo:', {
            fps: statistics.fps,
            points: statistics.totalPoints,
            visible: statistics.visiblePoints
          });
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [statistics]);

  return {
    containerRef,
    isLoading,
    error,
    tooltip,
    updateData,
    resetCamera,
    clearScene,
    exportImage,
    toggleAutoRotate,
    setSelectedMetrics,
    statistics
  };
};